import type { Debt, DebtPayment } from '../types/debt';

const STORAGE_KEY = 'debtracker_debts';

export const generateId = (): string =>
  Math.random().toString(36).substring(2, 10) + Date.now().toString(36);

// ---------- READ ----------
export const getAllDebts = (): Debt[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Debt[]) : [];
  } catch {
    return [];
  }
};

export const getDebtById = (id: string): Debt | undefined =>
  getAllDebts().find((d) => d.id === id);

// ---------- WRITE ----------
const saveAll = (debts: Debt[]) =>
  localStorage.setItem(STORAGE_KEY, JSON.stringify(debts));

export const createDebt = (debt: Omit<Debt, 'id' | 'createdAt' | 'updatedAt' | 'payments' | 'status' | 'amountPaid'>): Debt => {
  const now = new Date().toISOString();
  const newDebt: Debt = {
    ...debt,
    id: generateId(),
    amountPaid: 0,
    status: 'pending',
    payments: [],
    createdAt: now,
    updatedAt: now,
  };
  const all = getAllDebts();
  saveAll([...all, newDebt]);
  return newDebt;
};

export const updateDebt = (id: string, updates: Partial<Omit<Debt, 'id' | 'createdAt' | 'payments'>>): Debt | null => {
  const all = getAllDebts();
  const idx = all.findIndex((d) => d.id === id);
  if (idx === -1) return null;
  const updated: Debt = { ...all[idx], ...updates, updatedAt: new Date().toISOString() };
  all[idx] = updated;
  saveAll(all);
  return updated;
};

export const deleteDebt = (id: string): boolean => {
  const all = getAllDebts();
  const filtered = all.filter((d) => d.id !== id);
  if (filtered.length === all.length) return false;
  saveAll(filtered);
  return true;
};

// ---------- PAYMENTS ----------
export const addPayment = (debtId: string, payment: Omit<DebtPayment, 'id'>): Debt | null => {
  const all = getAllDebts();
  const idx = all.findIndex((d) => d.id === debtId);
  if (idx === -1) return null;

  const newPayment: DebtPayment = { ...payment, id: generateId() };
  const debt = all[idx];
  const newAmountPaid = debt.amountPaid + payment.amount;
  const newStatus = newAmountPaid >= debt.amount ? 'paid' : 'partial';

  all[idx] = {
    ...debt,
    amountPaid: newAmountPaid,
    status: newStatus,
    payments: [...debt.payments, newPayment],
    updatedAt: new Date().toISOString(),
  };
  saveAll(all);
  return all[idx];
};

// ---------- STATS ----------
export interface DebtStats {
  totalDebt: number;
  pendingCount: number;
  overdueCount: number;
  paidThisMonth: number;
}

export const getStats = (): DebtStats => {
  const all = getAllDebts();
  const now = new Date();
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString();

  let totalDebt = 0;
  let pendingCount = 0;
  let overdueCount = 0;
  let paidThisMonth = 0;

  for (const d of all) {
    if (d.status === 'paid') {
      for (const p of d.payments) {
        if (p.date >= startOfMonth) paidThisMonth += p.amount;
      }
      continue;
    }

    const remaining = d.amount - d.amountPaid;
    totalDebt += remaining;

    if (d.status === 'pending' || d.status === 'partial') {
      pendingCount++;
      if (d.dueDate && d.dueDate < now.toISOString().split('T')[0]) overdueCount++;
    }
  }

  return {
    totalDebt,
    pendingCount,
    overdueCount,
    paidThisMonth,
  };
};
